import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from 'src/environment';
import { PageResult } from "../shared/models/pageResult";
import { Approval, EnumRecordsTypes ,ApprovalSort } from "../models/approval.model";
import { DateRange, SelectedFilter } from "../shared/models/filter.model";
import { User } from "../models/user.model";
import { Expense } from "../models/Answer.model";
import { FileDownloadService } from "../shared/services/file-download.service";
import { ReportRow, ReportSort } from "../models/report.model";

export interface UpdateExpenseApprovalFields {
    clientCharge?: boolean | null;
    fullcharge?: boolean | null;
    clientChargeAmount?: number | null;
    approvedAmount?: number | null;
    commentAproval?: string | null;
}

export interface BulkUpdateApprovalStatusResultItem {
    guid: string;
    success: boolean;
    error: string | null;
}

@Injectable({
    providedIn: 'root'
})

export class ApprovalApiService {
    #httpservice = inject(HttpClient)
    #fileDownloadService = inject(FileDownloadService)
    #baseUrl = environment.apiUrl;
    #specBaseUrl = `${this.#baseUrl}/approvals`
    // Reports screen (DBJR-193): same service/DI instance as Approvals (unified with the rest of the
    // app rather than a separate ReportsApiService), but its own URL prefix - the server keeps these
    // endpoints under their own "reports" route group, see ApprovlsRoutes.cs.
    #reportsBaseUrl = `${this.#baseUrl}/reports`
    getApprovals = (page: number, selectedFilter: any, sort: ApprovalSort) => {
        return this.#httpservice.post<PageResult<Approval>>(`${this.#specBaseUrl}/getApprovals`, { paging: page, selectedFilter: selectedFilter,sortBy: sort.sortBy,
      sortDirection: sort.sortDirection });
    }

    getApprovalsExtra = (page: number, selectedFilter: any,sort: ApprovalSort) => {
        return this.#httpservice.post<PageResult<Approval>>(`${this.#specBaseUrl}/getExtraApprovals`, { paging: page, selectedFilter: selectedFilter,sortBy: sort.sortBy,
      sortDirection: sort.sortDirection });
    }

    // Sender-name filter options ("שם השולח"), scoped to the employees who submitted a request that
    // shows up in the current approvals view (tab + approver scoped) - unlike the global creator list.
    getSenderNamesList = (selectedFilter: SelectedFilter | null | undefined) => {
        return this.#httpservice.post<User[]>(`${this.#specBaseUrl}/getSenderNamesList`, selectedFilter ?? {});
    }

    // Total ApprovedAmount across all of one sender's approved requests, scoped to the same
    // tab/date-range/name filters currently applied on the approvals grid.
    getSenderApprovedTotal = (senderId: number, selectedFilter: SelectedFilter | null | undefined) => {
        return this.#httpservice.post<number>(`${this.#specBaseUrl}/getSenderApprovedTotal`, { senderId, selectedFilter: selectedFilter ?? {} });
    }

    // "חודש שכר" filter options for the After Approval tab: payroll months (yyyyMM, newest first).
    getPayrollMonthsList = () => {
        return this.#httpservice.get<number[]>(`${this.#specBaseUrl}/getPayrollMonthsList`);
    }

    updateApprovalStatus(guid: string, status: number, approvedAmount?: number, comment?: string) {
        return this.#httpservice.post<number>(`${this.#specBaseUrl}/UpdateApprovalStatus`, { guid: guid, status, approvedAmount, comment }
        );

    }

    getExpenseRows(guid: string) {
        return this.#httpservice.get<Expense[]>(`${this.#specBaseUrl}/getExpenseRows/${guid}`);
    }

    updateExpenseApprovalFields(expenseId: number, fields: UpdateExpenseApprovalFields) {
        return this.#httpservice.post<void>(`${this.#specBaseUrl}/updateExpenseApproval`, { expenseId, ...fields });
    }

    bulkUpdateApprovalStatus(guids: string[]) {
        return this.#httpservice.post<BulkUpdateApprovalStatusResultItem[]>(`${this.#specBaseUrl}/bulkUpdateApprovalStatus`, { guids });
    }

    getPayrollExportCount = () => {
        return this.#httpservice.get<number>(`${this.#specBaseUrl}/getPayrollExportCount`);
    }

    downloadRecord(selectedFilter: SelectedFilter | null | undefined, name: string, type: EnumRecordsTypes): void {
        this.#httpservice.post(
            `${this.#specBaseUrl}/downloadRecord?type=${type}`, selectedFilter,
            {
                responseType: 'blob',
                observe: 'response'
            }
        ).subscribe({
            next: (response) => this.#fileDownloadService.downloadBlobResponse(response, name),
            error: (err) => {
                console.error('Error downloading Excel file', err);
            }
        });
    }

    // --- Reports screen (DBJR-193) ---

    getReports = (page: number, selectedFilter: SelectedFilter | null | undefined, sort: ReportSort) => {
        return this.#httpservice.post<PageResult<ReportRow>>(`${this.#reportsBaseUrl}/getReports`, {
            paging: page, selectedFilter: selectedFilter, sortBy: sort.sortBy, sortDirection: sort.sortDirection
        });
    }

    getReportsExtra = (page: number, selectedFilter: SelectedFilter | null | undefined, sort: ReportSort) => {
        return this.#httpservice.post<PageResult<ReportRow>>(`${this.#reportsBaseUrl}/getExtraReports`, {
            paging: page, selectedFilter: selectedFilter, sortBy: sort.sortBy, sortDirection: sort.sortDirection
        });
    }

    // Sender-name filter options for Reports, scoped to the Reports base eligibility - distinct from
    // getSenderNamesList above, which is scoped to whichever Approvals tab is active.
    getReportsSenderNamesList = (selectedFilter: SelectedFilter | null | undefined) => {
        return this.#httpservice.post<User[]>(`${this.#reportsBaseUrl}/getSenderNamesList`, selectedFilter ?? {});
    }

    // Approver-name filter options ("מאשרים") for Reports' unified, multi-level approvers filter.
    getApproverNamesList = (selectedFilter: SelectedFilter | null | undefined) => {
        return this.#httpservice.post<User[]>(`${this.#reportsBaseUrl}/getApproverNamesList`, selectedFilter ?? {});
    }

    exportReportsToExcel(selectedFilter: SelectedFilter | null | undefined): void {
        this.#httpservice.post(
            `${this.#reportsBaseUrl}/exportToExcel`, selectedFilter ?? {},
            {
                responseType: 'blob',
                observe: 'response'
            }
        ).subscribe({
            next: (response) => this.#fileDownloadService.downloadBlobResponse(response, 'reports.xlsx'),
            error: (err) => {
                console.error('Error downloading Excel file', err);
            }
        });
    }
}